import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DoNotDisturbAltRoundedIcon from '@mui/icons-material/DoNotDisturbAltRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { alpha, Chip, useTheme } from '@mui/material';
import React from 'react';

import { JobStatus } from 'interfaces/job';
import { humanize } from 'utils/helpers';
import { COMPLETED_WITH_FAILURES_STATUS, IN_PROGRESS_STATUS } from './jobOutcomeSummary';

type JobStatusTone = 'success' | 'info' | 'warning' | 'error' | 'default';

interface JobStatusPresentation {
  tone: JobStatusTone;
  icon: React.ElementType;
}

const jobStatusPresentationByValue: Record<string, JobStatusPresentation> = {
  [JobStatus.Completed]: { tone: 'success', icon: CheckCircleRoundedIcon },
  [JobStatus.Running]: { tone: 'info', icon: SyncRoundedIcon },
  [JobStatus.Pending]: { tone: 'warning', icon: ScheduleRoundedIcon },
  [JobStatus.Failed]: { tone: 'error', icon: ErrorRoundedIcon },
  [JobStatus.FailedToStart]: { tone: 'error', icon: ErrorRoundedIcon },
  [JobStatus.Cancelled]: { tone: 'default', icon: CancelRoundedIcon },
  [COMPLETED_WITH_FAILURES_STATUS]: { tone: 'warning', icon: ErrorRoundedIcon },
  [IN_PROGRESS_STATUS]: { tone: 'info', icon: SyncRoundedIcon },
  skip: { tone: 'default', icon: DoNotDisturbAltRoundedIcon },
};

export const getJobStatusDisplayLabel = (status?: string, displayNames?: Record<string, string>) => {
  if (!status) {
    return 'Unknown';
  }

  if (displayNames?.[status]) {
    return displayNames[status];
  }

  if (status === 'skip') {
    return 'Skipped';
  }

  return humanize(status);
};

export const JobStatusChip: React.FC<{ status?: string; label?: string }> = ({ status, label }) => {
  const theme = useTheme();
  const presentation = jobStatusPresentationByValue[status || ''] || {
    tone: 'default' as const,
    icon: HelpOutlineRoundedIcon,
  };
  const StatusIcon = presentation.icon;
  const { tone } = presentation;
  const color = tone === 'default' ? theme.palette.text.secondary : theme.palette[tone].main;
  const textColor =
    tone === 'default'
      ? theme.palette.text.secondary
      : theme.palette.mode === 'light'
      ? theme.palette[tone].dark
      : theme.palette[tone].light;

  return (
    <Chip
      size="small"
      color={tone}
      variant="filled"
      icon={<StatusIcon fontSize="small" />}
      label={label || getJobStatusDisplayLabel(status)}
      sx={{
        height: 24,
        backgroundColor: tone === 'default' ? theme.palette.action.selected : alpha(color, 0.12),
        border: `1px solid ${tone === 'default' ? theme.palette.divider : alpha(color, 0.28)}`,
        color: textColor,
        '& .MuiChip-icon': {
          color: 'inherit',
          fontSize: 16,
          marginLeft: 0.5,
        },
        '& .MuiChip-label': {
          paddingInline: 0.75,
          fontSize: theme.typography.pxToRem(12),
          fontWeight: 600,
        },
      }}
    />
  );
};
